"use client";

import { useCallback } from "react";

import { useUser } from "@/contexts/user-provider";

import { patchMyMeta } from "@/api/provider/user/user";

// Beta: whether this user sees the dose log as the editable table (one column
// per field, grouped by day) instead of the classic list. Kept on their own
// profile (`user.meta.doseLogTable`) and read through the `doseLogTable` field
// on the current user: the raw meta bag is never sent to the client.
const PREFERENCE_KEY = "doseLogTable";

export function useDoseLogTable() {
  const { currentUser, setCurrentUser } = useUser();

  const isLoaded = currentUser !== undefined && currentUser !== null;
  const isOn = currentUser?.doseLogTable === true;

  const setOn = useCallback(
    async (value: boolean): Promise<boolean> => {
      if (!currentUser?.id) return false;
      const userId = currentUser.id;
      const previous = currentUser.doseLogTable;
      // Optimistic, so the dose log follows the switch straight away.
      setCurrentUser((user) =>
        user ? { ...user, doseLogTable: value } : user,
      );
      const result = await patchMyMeta({ [PREFERENCE_KEY]: value });
      if (result === null) {
        // The save failed: put the previous answer back, unless a different
        // user signed in while it was in flight.
        setCurrentUser((user) =>
          user && user.id === userId
            ? { ...user, doseLogTable: previous }
            : user,
        );
        return false;
      }
      return true;
    },
    [currentUser, setCurrentUser],
  );

  return { isLoaded, isOn, setOn };
}
